import type { CompletedAuthSteps, GetWebcastViewerNewAuthQuery } from '../../generated/graphql-viewer';
import type { SessionTokenJwt } from '../../utils/parse-jwt';

import { type ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';

import {
  AuthStepType,
  useCompleteAuthStepsMutation,
  useGetWebcastViewerNewAuthQuery,
} from '../../generated/graphql-viewer';
import { devLog, expiresAt } from '../../utils/dev-log';
import { getLocalStorageItem, removeLocalStorageItem } from '../../utils/local-storage';
import { parseJwt } from '../../utils/parse-jwt';

type AccessContextValue = {
  headers: Record<string, string>;
  hasAuthHeaders: boolean;
  sessionToken?: CompletedAuthSteps['sessionToken'];
  data?: GetWebcastViewerNewAuthQuery;
  currentAuthStep?: AuthStepType;
  accessDenied: boolean;
  hasUserEmail?: boolean;
  loading: boolean;
  invalidToken?: boolean;
  isPathInProgress?: boolean;
  refetch: () => void;
  setSessionToken: (sessionToken: CompletedAuthSteps['sessionToken']) => void;
  setCurrentAuthStep: (step?: AuthStepType) => void;
  setInvalidToken: (invalid: boolean) => void;
  setAccessDenied: (denied: boolean) => void;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
};

const initialContext: AccessContextValue = {
  headers: {},
  hasAuthHeaders: false,
  sessionToken: undefined,
  data: undefined,
  currentAuthStep: undefined,
  accessDenied: false,
  hasUserEmail: false,
  loading: false,
  invalidToken: false,
  isPathInProgress: false,
  refetch: () => {},
  setSessionToken: () => {},
  setCurrentAuthStep: () => {},
  setInvalidToken: () => {},
  setAccessDenied: () => {},
  startPolling: () => {},
  stopPolling: () => {},
};

export const AccessContext = createContext<AccessContextValue>(initialContext);

type Props = {
  children: ReactNode;
};

export function ViewerAccessProvider({ children }: Props) {
  const { webcastId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hmacToken = searchParams.get('hmac-token') || '';
  const timeoutRef = useRef(0);

  const [sessionToken, setSessionToken] = useState<CompletedAuthSteps['sessionToken']>();
  const [currentAuthStep, setCurrentAuthStep] = useState<AuthStepType>();
  const [accessDenied, setAccessDenied] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  const [isPathInProgress, setIsPathInProgress] = useState(false);

  const [completeAuthStepsMutation] = useCompleteAuthStepsMutation();

  const headers = useMemo((): Record<string, string> => {
    return sessionToken ? { 'X-Authorization-Token': sessionToken } : {};
  }, [sessionToken]);

  const hasAuthHeaders = Boolean(sessionToken);

  const { data, loading, refetch, startPolling, stopPolling } = useGetWebcastViewerNewAuthQuery({
    variables: { webcastId },
    skip: !webcastId,
    ...(hasAuthHeaders && { context: { headers } }),
  });

  const webcastNewAuth = data?.webcastNewAuth;
  const accessGranted = webcastNewAuth?.authenticationWorkflow?.accessGranted;
  const pathways = webcastNewAuth?.authenticationWorkflow?.pathways || [];
  const hasUserEmail = Boolean(webcastNewAuth?.authenticationWorkflow?.metadata?.email);

  const ssoAttempted = JSON.parse(getLocalStorageItem('lspro-viewer-sso-attempted') || 'false');
  const findPathInProgress = pathways.find((pathway) => pathway.isInProgress);
  const findSSOPathway = pathways.find((pathway) => pathway.requiredNextSteps.includes(AuthStepType.SSO_LOGIN));

  // Determine current authentication step
  useEffect(() => {
    if (findPathInProgress) {
      setCurrentAuthStep(findPathInProgress.requiredNextSteps?.[0]);
      setIsPathInProgress(true);
      return;
    }

    if (findSSOPathway && ssoAttempted) {
      setCurrentAuthStep(AuthStepType.SSO_LOGIN);
      return;
    }

    setCurrentAuthStep(undefined);
  }, [findPathInProgress, findSSOPathway, ssoAttempted]);

  useEffect(() => {
    if (currentAuthStep) devLog('Current authentication step:', currentAuthStep);
  }, [currentAuthStep]);

  // Protection token authentication
  useEffect(() => {
    if (!hmacToken && currentAuthStep === AuthStepType.PROVIDE_PROTECTION_TOKEN) {
      setAccessDenied(true);
      return;
    }

    if (hmacToken && currentAuthStep === AuthStepType.PROVIDE_PROTECTION_TOKEN) {
      completeAuthStepsMutation({
        variables: {
          input: {
            webcastId,
            steps: [{ authStepBody: { provideProtectionToken: { hmacToken } } }],
          },
        },
        ...(hasAuthHeaders && { context: { headers } }),
        onCompleted: (data) => {
          if (data.completeAuthSteps.__typename !== 'CompletedAuthSteps') return;

          if (data.completeAuthSteps.sessionToken) {
            setSessionToken(data.completeAuthSteps.sessionToken);
          }

          const failedStep = data.completeAuthSteps.failedSteps.find(
            (step) => step.stepType === AuthStepType.PROVIDE_PROTECTION_TOKEN
          );

          if (failedStep?.reason === 'ACCESS_DENIED') setAccessDenied(true);
        },
      });
    }
  }, [completeAuthStepsMutation, currentAuthStep, hasAuthHeaders, headers, hmacToken, webcastId]);

  // Referrer authentication
  useEffect(() => {
    if (currentAuthStep === AuthStepType.REFERRER && !accessDenied) {
      completeAuthStepsMutation({
        variables: {
          input: {
            webcastId,
            steps: [{ authStepBody: { referrer: {} } }],
          },
          ...(sessionToken && { context: { headers: { 'X-Authorization-Token': sessionToken } } }),
        },
        onCompleted: (data) => {
          if (data.completeAuthSteps.__typename !== 'CompletedAuthSteps') return;

          if (data.completeAuthSteps.sessionToken) {
            setSessionToken(data.completeAuthSteps.sessionToken);
          }

          const failedStep = data.completeAuthSteps.failedSteps.find((step) => step.stepType === AuthStepType.REFERRER);

          if (failedStep?.reason === 'ACCESS_DENIED') setAccessDenied(true);
        },
      });
    }
  }, [accessDenied, completeAuthStepsMutation, currentAuthStep, sessionToken, webcastId]);

  // Check for token expiration and set invalidToken state accordingly
  useEffect(() => {
    const token = parseJwt<SessionTokenJwt>(sessionToken);

    if (!currentAuthStep || !token) return;

    const expirationTime = token.exp * 1000;
    const timeout = expirationTime - Date.now();

    if (timeout <= 0) return;

    devLog('Scheduling setting session token ("X-Authorization-Token") as invalid at', expiresAt(timeout));

    clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setInvalidToken(true), timeout);
  }, [currentAuthStep, sessionToken]);

  useEffect(() => {
    if (sessionToken) refetch();
  }, [refetch, sessionToken]);

  useEffect(() => {
    if (accessGranted) {
      searchParams.delete('hmac-token');
      setSearchParams(searchParams);
      removeLocalStorageItem('lspro-viewer-sso-attempted');
    }
  }, [accessGranted, searchParams, setSearchParams]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return (
    <AccessContext.Provider
      value={{
        data,
        loading,
        sessionToken,
        headers,
        hasAuthHeaders,
        hasUserEmail,
        currentAuthStep,
        invalidToken,
        accessDenied,
        isPathInProgress,
        refetch,
        setCurrentAuthStep,
        setSessionToken,
        setInvalidToken,
        setAccessDenied,
        startPolling,
        stopPolling,
      }}
    >
      {children}
    </AccessContext.Provider>
  );
}

export const useViewerAccess = () => useContext(AccessContext);
